import * as React from 'react';
import { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, List, message, Modal, Row } from 'antd';
import CommonNotesChatPage from '../common_notes_chat_page/CommonNotesChatPage';
import CommonNoteInputForm from '../common_note_input_form/CommonNoteInputForm';
import OWButton from '../ow_button/OWButton';
import AvatarGroup from '../avatar_group/AvatarGroup';
import SelectUsers from '../select_users/SelectUsers';
import RemoveSubscriberButton from '../work_orders_detail_notes_page/RemoveSubscriberButton';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { withUserType } from '../withUserType';
import { WEB_SOCKET_ENTITY_TYPES } from '../../utils/WebSocketUtils';
import { owuploadCustomRequest } from '../../thunks/file_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getEntityById, getObjectValues, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { formatUserContact } from '../../utils/DataFormatterUtils';
import { debounce } from '../../utils/PerformanceUtils';
import {
	appendProjectNote,
	fetchProjectNotes,
	projectNotesChannelId,
} from '../../thunks/project_notes_thunks';
import {
	addProjectBuyerSubscribers,
	addProjectSupplierSubscribers,
	findProjectSubscribers,
	removeProjectBuyerSubscribers,
	removeProjectSupplierSubscribers,
} from '../../thunks/project_subscribers_thunks';

// Project Notes reuses the exact work-order / walkthrough notes UI
// (CommonNotesChatPage + CommonNoteInputForm) over the project_notes endpoints,
// reading and writing the shared state.messages chat store like they do.
// Behind projects-v2.
const WS_CONFIG = {
	entityIdName: 'projectId',
	entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_NOTES,
	newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER,
};

const ProjectNotesTab = ({
	currentUser,
	userType,
	match,
	messages,
	project,
	searchedSubscribers,
	fetchNotes,
	appendNote,
	findSubscribers,
	addBuyerSubscribers,
	addSupplierSubscribers,
	removeBuyerSubscribers,
	removeSupplierSubscribers,
}: any) => {
	const projectId = match.params.id;

	const [subscriberModalVisible, setSubscriberModalVisible] = useState(false);
	const [addSubscriberModalVisible, setAddSubscriberModalVisible] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
	const [selectValue, setSelectValue] = useState<any[]>([]);
	const [addingSubscribers, setAddingSubscribers] = useState(false);

	// CommonNotesChatPage clears its spinner in fetchNotes().then, with no catch, so a
	// rejection would leave the tab spinning forever. Nothing else surfaces the failure
	// either — messagesReducer has no FETCH_ALL_MESSAGES_ERROR case — so say it out
	// loud, otherwise an unreachable history is indistinguishable from an empty one.
	const loadNotes = useCallback(
		() =>
			fetchNotes(projectId).catch((error) => {
				message.error(
					typeof error === 'string' && error ? error : "Couldn't load this project's notes."
				);
				return [];
			}),
		[fetchNotes, projectId]
	);
	const sendNote = useCallback((note: any) => appendNote(projectId, note), [appendNote, projectId]);

	// The project's effective subscribers, hydrated by the API on GET-by-id (which the
	// parent detail page already does). Buyer and supplier contacts share one list here;
	// contactType is what decides which endpoint a removal goes to.
	const subscribers = nullSafeGetOrElse('buyerSubscribers', project, []).concat(
		nullSafeGetOrElse('supplierSubscribers', project, [])
	);
	const filteredUsers = getObjectValues(nullSafeGetOrElse('records', searchedSubscribers, {})).map(
		(contact) => formatUserContact(contact)
	);

	// @-mention autocomplete has to hit the project's own candidate endpoint — the
	// shared ChatInput defaults to the work-order one.
	const fetchMentionUsers = useCallback(
		(searchText: string) => findSubscribers(projectId, searchText && searchText.trim()),
		[findSubscribers, projectId]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFindSubscribers = useCallback(
		debounce((searchText: string) => findSubscribers(projectId, searchText), 400, false),
		[findSubscribers, projectId]
	);

	const handleSearchDebounced = useCallback(
		(value = '') => {
			const searchText = value && value.trim();
			setSearch(searchText);
			debouncedFindSubscribers(searchText);
		},
		[debouncedFindSubscribers]
	);

	const handleOnChange = useCallback((value: any) => {
		const updated = Array.isArray(value)
			? value.map((one) => JSON.parse(one))
			: [JSON.parse(value)];
		setSelectValue(updated.map((one) => JSON.stringify(one)));
		setSelectedUsers(updated);
	}, []);

	// Returned so RemoveSubscriberButton can spin until the call settles. The response
	// is the re-hydrated project, so the avatar group updates without a refetch.
	const handleSubscriberRemoved = useCallback(
		(item: any) => () =>
			item.contactType === ROLE_TYPES.BUYER
				? removeBuyerSubscribers(projectId, [item.email], project)
				: removeSupplierSubscribers(projectId, [item.email], project),
		[removeBuyerSubscribers, removeSupplierSubscribers, projectId, project]
	);

	const handleAddSubscribers = useCallback(() => {
		setAddingSubscribers(true);
		const buyerEmails = selectedUsers
			.filter((one) => one.contactType === ROLE_TYPES.BUYER)
			.map((one) => one.email);
		const supplierEmails = selectedUsers
			.filter((one) => one.contactType !== ROLE_TYPES.BUYER)
			.map((one) => one.email);
		// Sequential, not Promise.all: each call answers with the whole project, and the
		// thunk writes that into the projects slice — so two in flight together let the
		// slower response overwrite the other's subscriber list. Chained, the last
		// response is the one carrying both additions.
		const addBuyers = () =>
			buyerEmails.length ? addBuyerSubscribers(projectId, buyerEmails, project) : Promise.resolve();
		const addSuppliers = () =>
			supplierEmails.length
				? addSupplierSubscribers(projectId, supplierEmails, project)
				: Promise.resolve();
		addBuyers()
			.then(addSuppliers)
			.then(() => {
				// Only clear and close on success — otherwise a failure looks identical to a
				// success and the user loses the contacts they picked.
				setSelectValue([]);
				setSelectedUsers([]);
				setAddSubscriberModalVisible(false);
			})
			.catch((error) =>
				message.error(
					typeof error === 'string' && error
						? error
						: 'Failed to add subscribers. Please try again.'
				)
			)
			.finally(() => setAddingSubscribers(false));
	}, [selectedUsers, addBuyerSubscribers, addSupplierSubscribers, projectId, project]);

	const openAddSubscribers = useCallback(() => {
		setSubscriberModalVisible(false);
		setAddSubscriberModalVisible(true);
		setSearch('');
		setSelectedUsers([]);
		setSelectValue([]);
	}, []);

	const renderSubscribers = useCallback(
		() => (
			<>
				<div style={{ textAlign: 'right', padding: '0', height: '0px' }}>
					<Modal
						width={600}
						title={
							<div>
								<span>Project Subscribers</span>
								<Button
									style={{ float: 'right', marginRight: '32px', marginTop: '-4px' }}
									onClick={openAddSubscribers}
									type="primary"
								>
									Add People
								</Button>
							</div>
						}
						footer={null}
						closable={true}
						onCancel={() => setSubscriberModalVisible(false)}
						visible={subscriberModalVisible}
					>
						<List
							itemLayout="horizontal"
							dataSource={subscribers}
							renderItem={(item: any) => (
								<List.Item>
									<List.Item.Meta
										avatar={
											<ContactAvatar
												hidePopover={true}
												placement="bottomRight"
												userType={userType}
												contact={item}
											/>
										}
										// @ts-ignore
										title={`${item.nameGiven} ${item.nameFamily}`}
										// @ts-ignore
										description={item.email}
									/>
									<RemoveSubscriberButton
										subscriber={item}
										onRemove={handleSubscriberRemoved}
										workOrder={project}
										// A project has no assigned supplier facility, so the shared button's
										// "protect that facility's staff from buyer removal" rule has nothing to
										// compare against — without this it silently hides Remove for any
										// subscriber whose contact carries no facilityId.
										noAssignedSupplierProtection
									/>
								</List.Item>
							)}
						/>
					</Modal>
					<Modal
						width={600}
						title={<span>Add Someone to this Project</span>}
						footer={
							<div style={{ display: 'block', width: '100%' }}>
								{/* OWButton, not a raw Button: this writes on click, and `loading` alone
								    cannot stop a fast double-click because setAddingSubscribers is async.
								    OWButton ref-guards the click. */}
								<OWButton type="primary" loading={addingSubscribers} onClick={handleAddSubscribers}>
									Add Subscribers
								</OWButton>
							</div>
						}
						closable={true}
						onCancel={() => setAddSubscriberModalVisible(false)}
						visible={addSubscriberModalVisible}
					>
						<Row key={0}>
							{/* @ts-ignore */}
							<SelectUsers
								mode="multiple"
								style={{ width: '100%' }}
								placeholder="Begin to type a name or email to start searching"
								onSearch={handleSearchDebounced}
								onFocus={() => handleSearchDebounced()}
								onChange={handleOnChange}
								search={search}
								selectValue={selectValue}
								filteredUsers={filteredUsers}
								disabledUsers={subscribers}
							/>
						</Row>
					</Modal>
				</div>
				{/* @ts-ignore */}
				<AvatarGroup
					avatars={subscribers}
					onClick={() => setSubscriberModalVisible(true)}
					onAddClick={openAddSubscribers}
				/>
			</>
		),
		[
			subscribers,
			subscriberModalVisible,
			addSubscriberModalVisible,
			addingSubscribers,
			search,
			selectValue,
			filteredUsers,
			userType,
			project,
			openAddSubscribers,
			handleSubscriberRemoved,
			handleAddSubscribers,
			handleSearchDebounced,
			handleOnChange,
		]
	);

	const renderInputForm = useCallback(
		({ handleTyping, onSendNote, typingDetails, newNoteCount, onRefresh }) => (
			<CommonNoteInputForm
				appendNote={sendNote}
				currentUser={currentUser}
				onTyping={handleTyping}
				onSendNote={onSendNote}
				typingDetails={typingDetails}
				newNoteCount={newNoteCount}
				onRefresh={onRefresh}
				fetchMentionUsers={fetchMentionUsers}
				// Media: without this, photo/video/audio notes fall back to
				// workOrderChatUploadCustomRequest and ask for a presigned URL at
				// /file/work_order_chat/upload/get_presigned_url/undefined. Projects have no
				// note-specific upload endpoint, so use the generic one — the form BidderNotes
				// uses. userType is supplied by the callers of this function.
				customUploadRequest={owuploadCustomRequest}
			/>
		),
		[sendNote, currentUser, fetchMentionUsers]
	);

	return (
		<CommonNotesChatPage
			entityId={projectNotesChannelId(projectId)}
			fetchNotes={loadNotes}
			userType={userType}
			heightSelector="detailPage"
			wsConfig={WS_CONFIG}
			currentUser={currentUser}
			messages={messages}
			renderSubscribers={renderSubscribers}
			renderInputForm={renderInputForm}
		/>
	);
};

const mapStateToProps = (state: any, ownProps: any) => ({
	currentUser: state.session.currentUser,
	messages: state.messages,
	// Subscribers ride on the project, hydrated by readOne — which the parent detail
	// page already issues. Keyed by the URL id, never the slice's shared detail slot.
	project: getEntityById(ownProps.match.params.id, state.projects),
	searchedSubscribers: state.searched_subscribers,
});

const mapDispatchToProps = (dispatch, ownProps: any) => {
	// Every thunk here is a role factory, so the role is applied once from this
	// component's own userType — no per-call ForBuyer/ForSupplier choice to get wrong.
	const { userType } = ownProps;
	return {
		fetchNotes: (projectId) => dispatch(fetchProjectNotes(userType)(projectId)),
		appendNote: (projectId, note) => dispatch(appendProjectNote(userType)(projectId, note)),
		findSubscribers: (projectId, search) =>
			dispatch(findProjectSubscribers(userType)(projectId, search)),
		addBuyerSubscribers: (projectId, emails, project) =>
			dispatch(addProjectBuyerSubscribers(userType)(projectId, emails, project)),
		addSupplierSubscribers: (projectId, emails, project) =>
			dispatch(addProjectSupplierSubscribers(userType)(projectId, emails, project)),
		removeBuyerSubscribers: (projectId, emails, project) =>
			dispatch(removeProjectBuyerSubscribers(userType)(projectId, emails, project)),
		removeSupplierSubscribers: (projectId, emails, project) =>
			dispatch(removeProjectSupplierSubscribers(userType)(projectId, emails, project)),
	};
};

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(ProjectNotesTab))
);
